import { useEffect, useState } from "react";
import { uploadTalentProfilePhoto } from "../../../../services/talentCalls";
import { errorHandler } from "../../../../utils";
import { useNavigate } from "react-router";
import { convertToBase64, getNameInitials } from "../../../../utils/functions";
import moment from "moment";

import {
  IconPencil,
  IconShare3,
  IconDownload,
  IconCircleCheck,
  IconMapPin,
  IconBriefcase,
  IconEye,
} from "@tabler/icons-react";
import styles from "../../../../pages/TalentDashboard/Profile/profile.module.css";
import useRadialGauge from "../../../../hooks/useRadialGauge";
import { Link } from "react-router-dom";
import { checklistConfig } from "../components/data";

const RING_RADIUS = 48;

const TalentProfileHeader = ({ data, ringPercent, onShare }) => {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [load, setLoad] = useState(false);
  const [talentID, setTalentID] = useState("");
  const isCompleted = (value) => value === "Yes" || value === true;
  const strengthChecklist = checklistConfig.map(([label, key]) => ({
    label,
    done: isCompleted(data?.[key]),
  }));

  const completed =
    strengthChecklist?.filter((item) => item?.done)?.length || 0;
  const total = strengthChecklist?.length || 0;
  const percent = total === 0 ? 0 : Math.round((completed / total) * 100);

  const { circumference, offset, displayPercent } = useRadialGauge(
    RING_RADIUS,
    percent,
    1400,
    "mount",
  );

  useEffect(() => {
    if (data?._id) {
      setTalentID(data?._id);
    }
  }, [data]);

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setFile(file);
    await handleSubmit(file);
  };

  const handleSubmit = async (file) => {
    if (!file || !talentID) return;

    try {
      setLoad(true);

      const base64Image = await convertToBase64(file);
      const data = { avatar: base64Image };

      await uploadTalentProfilePhoto(talentID, data);
      setLoad(false);
    } catch (error) {
      setLoad(false);

      error?.response?.data
        ? errorHandler(error?.response?.data?.errors, "Error")
        : errorHandler(
            "Network error: Something went wrong. Please check your internet connection and try again.",
            "Error",
          );
    }
  };

  return (
    <>
      <div className={styles.heroCard}>
        <div className={styles.heroBanner}>
          <div className={styles.heroActions}>
            <Link
              to="/talent-onboarding?stage=1"
              className={`${styles.heroBtn} ${styles.heroBtnOutline}`}
            >
              <IconPencil size={14} /> Edit Profile
            </Link>
            <button
              type="button"
              className={styles.heroIconBtn}
              aria-label="Share profile"
              onClick={onShare}
            >
              <IconShare3 size={16} />
            </button>
            {data?.pdfUrl && (
              <a
                href={data.pdfUrl}
                download
                target="_blank"
                rel="noopener noreferrer"
                className={`${styles.heroBtn} ${styles.heroBtnSolid}`}
              >
                <IconDownload size={14} /> Download Resume
              </a>
            )}
          </div>
        </div>

        <div className={styles.heroBody}>
          <div className={styles.avatarWrap}>
            <svg
              className={styles.avatarRing}
              viewBox="0 0 104 104"
              width="104"
              height="104"
            >
              <circle
                className={styles.avatarRingTrack}
                cx="52"
                cy="52"
                r={RING_RADIUS}
                fill="none"
                strokeWidth="5"
              />
              <circle
                className={styles.avatarRingProgress}
                cx="52"
                cy="52"
                r={RING_RADIUS}
                fill="none"
                strokeWidth="5"
                strokeLinecap="round"
                strokeDasharray={circumference.toFixed(2)}
                strokeDashoffset={offset.toFixed(2)}
              />
            </svg>
            <div className={styles.avatar}>
              {data?.avatar || file ? (
                <label
                  htmlFor="upload_file"
                  className="w-full h-full inline-block"
                >
                  <img
                    src={file ? URL.createObjectURL(file) : data?.avatar}
                    alt=""
                    className="w-full h-full object-cover rounded-full"
                  />
                </label>
              ) : (
                <>
                  <label
                    htmlFor="upload_file"
                    className={`flex items-center justify-center w-full h-full`}
                  >
                    {getNameInitials(data?.firstName, data?.lastName)}
                  </label>
                </>
              )}
              <input
                id="upload_file"
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                style={{ display: "none" }}
              />
            </div>
            <div className={styles.ringBadge}>{displayPercent}%</div>
          </div>

          <div className={styles.heroInfo}>
            <div className={styles.heroNameRow}>
              <span className={styles.heroName}>
                {" "}
                {data?.firstName || data?.lastName
                  ? `${data?.firstName || ""} ${data?.lastName || ""}`
                  : "User"}
              </span>
              {data?.profileVerified && (
                <span
                  className={`${styles.heroBadge} ${styles.heroBadgeVerified}`}
                >
                  <IconCircleCheck size={12.5} /> Verified
                </span>
              )}
              {data?.workMode?.some(
                (mode) => mode.toLowerCase() === "work remotely",
              ) && (
                <span className={`${styles.heroBadge} ${styles.heroBadgeOpen}`}>
                  <span className={styles.heroBadgeDot} /> Open to Remote Work
                </span>
              )}
            </div>
            <div className={styles.heroHeadline}>{data?.pryRole}</div>
            <div className={styles.heroMeta}>
              {data?.currentLocation && (
                <span className={styles.heroMetaItem}>
                  <IconMapPin size={13.5} /> {data?.currentLocation}
                </span>
              )}
              {data?.dateOfBirth && (
                <span className={styles.heroMetaItem}>
                  <IconBriefcase size={13.5} />{" "}
                  {moment().diff(moment(data?.dateOfBirth), "years")} years
                </span>
              )}
              {data?.profileViewsLabel && (
                <span className={styles.heroMetaItem}>
                  <IconEye size={13.5} /> {data?.profileViewsLabel}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default TalentProfileHeader;
